import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import OutcomeModal from './OutcomeModal';

// ── Badges ────────────────────────────────────────────────────────────────────

function riskBadge(level) {
  if (level === 'at_risk') return <span className="badge badge-red">At Risk</span>;
  if (level === 'warning')  return <span className="badge badge-orange">Watch</span>;
  return <span className="badge badge-green">On Track</span>;
}

function dayBadge(day, checkpointDay) {
  const d = checkpointDay || day;
  return <span className="badge badge-gray">Day {d}</span>;
}

function visitBadge(visits) {
  const color = visits === 0 ? 'badge-red' : visits < 3 ? 'badge-orange' : 'badge-green';
  return <span className={`badge ${color}`}>{visits} visit{visits !== 1 ? 's' : ''}</span>;
}

function verifyBadge(overdue) {
  return overdue
    ? <span className="badge badge-red">Verify ID — Overdue</span>
    : <span className="badge badge-yellow">Verify ID</span>;
}

// ── Section config ────────────────────────────────────────────────────────────
// Returns the ordered sections array from the API response object.
// Day sections use different label/icon per checkpoint day.

function buildSections(data) {
  return [
    {
      key:   'atRisk',
      label: '🔴 At Risk',
      items: data.atRisk || [],
    },
    {
      key:   'day12',
      label: '📅 Day 12 Today — Close Window',
      items: data.day12 || [],
      checkpointDay: 12,
    },
    {
      key:   'day9',
      label: '📅 Day 9 Today — Intervention Window',
      items: data.day9 || [],
      checkpointDay: 9,
    },
    {
      key:   'day5',
      label: '📅 Day 5 Today — Mid-Trial Check',
      items: data.day5 || [],
      checkpointDay: 5,
    },
    {
      key:   'day3',
      label: '📅 Day 3 Today — Early Check-In',
      items: data.day3 || [],
      checkpointDay: 3,
    },
    {
      key:   'verification',
      label: '🪪 Pending Verification',
      items: data.verification || [],
    },
    {
      key:   'contacts',
      label: "📞 Today's Contacts",
      items: data.contacts || [],
    },
    {
      key:   'manager',
      label: '🤝 Manager Connections',
      items: data.manager || [],
    },
  ];
}

// ── Main component ────────────────────────────────────────────────────────────

export default function T2MCheckpoint({ onSelectTrial }) {
  const { get }        = useApi();
  const { currentClub } = useClub();

  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [expandedId, setExpandedId]   = useState(null);
  const [outcomeTarget, setOutcomeTarget] = useState(null);
  const [completed, setCompleted]     = useState([]);
  const [search, setSearch]           = useState('');

  function load() {
    if (!currentClub?.id) return;
    setLoading(true);
    get(`/t2m/checkpoint?club=${currentClub.id}`)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [currentClub?.id]);

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (!data)   return <div className="empty-state"><p>No checkpoint data.</p></div>;

  const sections      = buildSections(data);
  const completedIds  = new Set(completed.map(c => c.id));
  const searchLower   = search.toLowerCase();
  const isSearching   = search.trim().length > 0;

  function allItems() {
    return sections.flatMap(s => s.items);
  }

  function matchesSearch(trial) {
    return (
      trial.name?.toLowerCase().includes(searchLower) ||
      trial.email?.toLowerCase().includes(searchLower) ||
      trial.phone?.includes(search)
    );
  }

  function handleToggle(id) {
    setExpandedId(prev => prev === id ? null : id);
  }

  function handleLog(trial) {
    setExpandedId(trial.id);
    setOutcomeTarget(trial);
  }

  function handleOutcomeSaved(trial, outcome) {
    setOutcomeTarget(null);
    setExpandedId(null);
    setCompleted(prev => [...prev.filter(c => c.id !== trial.id), { ...trial, outcome }]);
    load();
  }

  const totalPending = sections.reduce(
    (n, s) => n + s.items.filter(i => !completedIds.has(i.id)).length,
    0
  );

  // Summary bar counts
  const { summary } = data;

  return (
    <div>
      {/* Summary bar */}
      {summary && (
        <div style={{
          display: 'flex', gap: '1rem', flexWrap: 'wrap',
          marginBottom: '1.25rem', padding: '0.75rem 1rem',
          background: 'var(--surface-2)', borderRadius: '8px',
          fontSize: '0.82rem', color: 'var(--text-secondary)'
        }}>
          <span>🗂 <strong style={{ color: 'var(--text)' }}>{summary.total}</strong> active trials</span>
          {summary.atRisk > 0 && (
            <span>🔴 <strong style={{ color: 'var(--red)' }}>{summary.atRisk}</strong> at risk</span>
          )}
          {summary.checkpointToday > 0 && (
            <span>📅 <strong style={{ color: 'var(--yellow)' }}>{summary.checkpointToday}</strong> checkpoint{summary.checkpointToday !== 1 ? 's' : ''} today</span>
          )}
          {summary.pendingVerification > 0 && (
            <span>🪪 <strong style={{ color: 'var(--text)' }}>{summary.pendingVerification}</strong> need ID</span>
          )}
          {summary.todayContacts > 0 && (
            <span>📞 <strong style={{ color: 'var(--text)' }}>{summary.todayContacts}</strong> to contact</span>
          )}
        </div>
      )}

      {/* Search bar */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          className="field-input"
          type="text"
          placeholder="Search by name, phone, email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>

      {/* Search results */}
      {isSearching && (
        <div>
          <div className="section-header">🔍 Results for "{search}"</div>
          {allItems()
            .filter(t => !completedIds.has(t.id) && matchesSearch(t))
            .map(trial => (
              <TrialCard
                key={`search-${trial.id}`}
                trial={trial}
                expanded={expandedId === trial.id}
                onToggle={() => handleToggle(trial.id)}
                onLog={() => handleLog(trial)}
                onView={() => onSelectTrial(trial.id)}
              />
            ))}
        </div>
      )}

      {/* Normal sections */}
      {!isSearching && (
        <>
          {totalPending === 0 && completed.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">✅</div>
              <div className="empty-state-text">All clear — nothing needs attention today.</div>
            </div>
          )}

          {sections.map(({ key, label, items, checkpointDay }) => {
            const active = items.filter(i => !completedIds.has(i.id));
            if (active.length === 0) return null;
            return (
              <div key={key} style={{ marginBottom: '1.5rem' }}>
                <div className="section-header">{label} · {active.length}</div>
                {active.map(trial => (
                  <TrialCard
                    key={`${key}-${trial.id}`}
                    trial={trial}
                    checkpointDay={checkpointDay}
                    expanded={expandedId === trial.id}
                    onToggle={() => handleToggle(trial.id)}
                    onLog={() => handleLog(trial)}
                    onView={() => onSelectTrial(trial.id)}
                  />
                ))}
              </div>
            );
          })}

          {/* Completed */}
          {completed.length > 0 && (
            <div style={{ marginBottom: '1.5rem', opacity: 0.6 }}>
              <div className="section-header">✅ Completed · {completed.length}</div>
              {completed.map(trial => (
                <div
                  key={trial.id}
                  className="card"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem' }}
                >
                  <span className="card-name" style={{ fontSize: '0.95rem' }}>{trial.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span className="badge badge-green">{trial.outcome}</span>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => setCompleted(prev => prev.filter(c => c.id !== trial.id))}
                    >
                      ↩ They Responded
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {outcomeTarget && (
        <OutcomeModal
          trial={outcomeTarget}
          onClose={() => setOutcomeTarget(null)}
          onSaved={(outcome) => handleOutcomeSaved(outcomeTarget, outcome)}
        />
      )}
    </div>
  );
}

// ── TrialCard ─────────────────────────────────────────────────────────────────

function TrialCard({ trial, checkpointDay, expanded, onToggle, onLog, onView }) {
  const riskClass = trial.riskLevel === 'at_risk' ? 'at-risk'
                  : trial.riskLevel === 'warning'  ? 'warning'
                  : 'ok';

  return (
    <div className={`card ${riskClass}`} style={{ cursor: 'pointer' }} onClick={onToggle}>
      <div className="card-header">
        <span className="card-name">{trial.name}</span>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {riskBadge(trial.riskLevel)}
          {dayBadge(trial.trialDay, checkpointDay)}
          {visitBadge(trial.visits)}
          {/* Show overdue verify badge inline on card header */}
          {trial.verificationOverdue && verifyBadge(true)}
        </div>
      </div>

      {expanded && (
        <div onClick={e => e.stopPropagation()}>
          <div
            className="card-meta"
            style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 1.5rem' }}
          >
            {trial.phone && <span>📱 {trial.phone}</span>}
            {trial.email && <span>✉️ {trial.email}</span>}
            <span>📅 Started {trial.startDate}</span>
            <span>🏠 Club {trial.clubId}</span>
            {trial.skillLevel  && <span>🎯 {trial.skillLevel}</span>}
            {trial.nextAction  && <span>→ Next: {trial.nextAction}</span>}
            {trial.lastContactDate && <span>📞 Last contact {trial.lastContactDate}</span>}
            {/* Verification badge — show pending (not overdue) in expanded view */}
            {trial.verificationStatus === 'pending' && !trial.verificationOverdue && (
              <span style={{ gridColumn: '1 / -1' }}>
                {verifyBadge(false)}
              </span>
            )}
            {trial.verificationOverdue && (
              <span style={{ gridColumn: '1 / -1' }}>
                {verifyBadge(true)}
              </span>
            )}
            {trial.notes && (
              <span style={{ gridColumn: '1 / -1', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                📝 {trial.notes}
              </span>
            )}
          </div>

          {/* Contact method guidance by checkpoint day */}
          {checkpointDay && (
            <div style={{
              marginTop: '0.75rem', padding: '0.5rem 0.75rem',
              background: 'var(--surface-3)', borderRadius: '6px',
              fontSize: '0.82rem', color: 'var(--text-secondary)'
            }}>
              {checkpointDay === 3  && '💬 Day 3: Text or Call — confirm they\'re enjoying it'}
              {checkpointDay === 5  && '📞 Day 5: Call preferred, Text if no answer — rescue visit'}
              {checkpointDay === 9  && '📞 Day 9: Call preferred — intervention window'}
              {checkpointDay === 12 && '📞 Day 12: Call — 3 days left, close the membership'}
            </div>
          )}

          <div className="card-actions" style={{ marginTop: '0.75rem' }}>
            <button className="btn btn-primary btn-sm" onClick={onLog}>Log Contact</button>
            <button className="btn btn-ghost btn-sm" onClick={onView}>View Record</button>
            <button className="btn btn-ghost btn-sm" onClick={onToggle}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
